import { isNil } from 'lodash-es';
import { Resource } from '../Resource';
import * as methods from '../methods';
import * as transformers from '../transformers';
import { queryParamArrayAsString } from '../utils';
import { plotMutationExternalInvalidationKeys } from './plots';
const rootPath = 'devices';
export const fromRaw = ({ available_measures, brand, created_at, current_events, current_message_quality, distance_from, enduser_name, id, last_battery_change, last_located_at, last_measured_at, location, location_text, message_quality, model, network, organization, origin_name, origin, plots, serial, stations, tzinfo, broadcast_network_provider, rotronic_probe_change, shared }) => ({
    kind: 'device',
    availableMeasures: available_measures ?? [],
    brand,
    createdAt: created_at,
    currentEvents: current_events,
    currentMessageQuality: current_message_quality,
    distanceFrom: distance_from,
    endUserName: enduser_name,
    id,
    lastBatteryChange: last_battery_change,
    lastLocatedAt: last_located_at,
    lastMeasuredAt: last_measured_at,
    location,
    messageQuality: message_quality,
    model,
    networkId: network,
    organizationId: organization,
    originName: !isNil(origin_name) ? origin_name : null,
    origin,
    plotIds: plots,
    stationIds: stations,
    reverseGeocoding: location_text,
    serial,
    timezone: tzinfo,
    broadcastNetwork: broadcast_network_provider,
    rotronicProbeChange: rotronic_probe_change,
    shared: shared
});
const fromRawDeviceEvent = (raw) => ({
    faultType: raw.fault_type,
    createdAt: raw.created_at,
    deviceId: raw.device_id,
    end: raw.end,
    start: raw.start
});
const toRaw = ({ serial, organizationId, plotIds = [] }) => ({
    serial,
    organization: organizationId,
    plots: plotIds
});
const toRawLinkPlots = ({ plotIds }) => ({
    plots: plotIds
});
const fromRawLinkPlots = ({ device, plots }) => ({
    deviceId: device,
    plotIds: plots
});
const toBatteryChangeRaw = ({ changeDate }) => ({
    change_date: changeDate
});
export const deviceMutationExternalInvalidationKeys = new Set();
const devices = Resource((requesterBuilder) => {
    const getMany = (query) => requesterBuilder({
        path: rootPath,
        requestCtx: {
            transform: transformers.paginatedResponse(fromRaw),
            params: {
                query: {
                    sort_by: query?.sortBy,
                    sort_order: query?.sortOrder,
                    offset: query?.offset,
                    limit: query?.limit,
                    organization_context: query?.viewableByOrganizationId,
                    organization: query?.ownedByOrganizationId,
                    q: query?.q,
                    id__in: query?.ids,
                    fault_type: queryParamArrayAsString(query?.faultTypes),
                    metrics__in: query?.metrics,
                    metrics_op: query?.metricOperator,
                    brand: query?.brand,
                    distance_from_lat: query?.distanceFrom?.lat,
                    distance_from_lng: query?.distanceFrom?.lng,
                    current_events: query?.currentEvents,
                    message_quality: query?.messageQuality,
                    model: query?.model,
                    sensor_category: query?.category,
                    has_active_links: query?.hasActiveLinks,
                    shared: query?.shared,
                    plot: query?.plot
                }
            }
        }
    });
    plotMutationExternalInvalidationKeys.add(getMany().invalidationKey[0]);
    const getEvents = (query) => methods.get({
        rootPath: `device/events`,
        requesterBuilder,
        fromRaw: (res) => res.map(fromRawDeviceEvent),
        params: {
            query: {
                fault_type: query?.faultType,
                since: query?.since,
                before: query?.before,
                device: query?.deviceId,
                current_events: query?.currentEvents
            }
        }
    })();
    const getCSV = (organizationId) => requesterBuilder({
        path: `${rootPath}/export`,
        requestCtx: {
            method: 'GET',
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            parse: (x) => x.blob(),
            transform: (blob) => blob,
            params: {
                query: {
                    organization_context: organizationId
                }
            }
        }
    });
    const get = (deviceId, query) => methods.get({
        fromRaw,
        requesterBuilder,
        rootPath,
        cacheConfig: {
            staleTime: 60 * 1000
        },
        params: {
            query: {
                organization_context: query?.viewableByOrganizationId,
                current_events: query?.currentEvents,
                message_quality: query?.messageQuality
            }
        }
    })(deviceId);
    const getBatteryHistory = (deviceId) => {
        return requesterBuilder({
            path: `${rootPath}/${deviceId}/battery-history`,
            requestCtx: {
                method: 'GET',
                transform: (x) => x
            }
        });
    };
    const postBatteryChange = (deviceId) => {
        return requesterBuilder({
            path: `${rootPath}/${deviceId}/battery-change`,
            requestCtx: {
                method: 'POST',
                toRaw: toBatteryChangeRaw,
                parse: async (x) => x
            },
            keysToInvalidate: [getBatteryHistory(deviceId).invalidationKey]
        });
    };
    const deleteBatteryChange = (deviceId) => {
        return requesterBuilder({
            path: `${rootPath}/${deviceId}/battery-change-delete`,
            requestCtx: {
                method: 'POST',
                toRaw: toBatteryChangeRaw,
                parse: async (x) => x
            },
            keysToInvalidate: [getBatteryHistory(deviceId).invalidationKey]
        });
    };
    const patchSharedStatus = (deviceId) => {
        return requesterBuilder({
            path: `${rootPath}/${deviceId}/shared-status`,
            requestCtx: {
                method: 'PATCH',
                parse: async (x) => x
            },
            keysToInvalidate: [getMany().invalidationKey, get(deviceId).invalidationKey]
        });
    };
    const linkPlots = (deviceId) => {
        return requesterBuilder({
            path: `${rootPath}/${deviceId}/add_plots_links`,
            requestCtx: {
                method: 'POST',
                toRaw: toRawLinkPlots,
                transform: fromRawLinkPlots
            }
        });
    };
    const getAvailableModels = (query) => {
        return requesterBuilder({
            path: `${rootPath}/available-models`,
            requestCtx: {
                method: 'GET',
                transform: (x) => x,
                params: {
                    query: {
                        organization_context: query?.organizationId
                    }
                }
            }
        });
    };
    const buildKeysToInvalidate = () => [
        getMany().invalidationKey,
        getAvailableModels().invalidationKey,
        ...Array.from(deviceMutationExternalInvalidationKeys).map((k) => [k])
    ];
    return {
        create: methods.create({
            requesterBuilder,
            rootPath,
            fromRaw,
            toRaw,
            buildKeysToInvalidate
        }),
        /** Those methods require the 'x-weenat-target-org' header to be set */
        get,
        getBatteryHistory,
        postBatteryChange,
        deleteBatteryChange,
        patchSharedStatus,
        getMany,
        getAll: methods.getAllPages({ getMany }),
        getPage: methods.getPage({ getMany }),
        getEvents,
        linkPlots,
        delete: methods.deletion({
            requesterBuilder,
            rootPath,
            buildKeysToInvalidate
        }),
        download: getCSV,
        getAvailableModels
    };
});
export default devices;
